import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { AlertNote } from "./AlertNote";
import { Button } from "./Button";
import { UnifiedModal } from "./UnifiedModal";
import { useAuth } from "./contexts/AuthContext";
import { PLAN_PATH } from "../Pages/Settings/settingsPaths";
import { PLAN_NAMES } from "../Pages/Plan/billingFormat";
import { PLANS_ON_SALE } from "../utils/agencyFeatures";

/**
 * Aviso al chocar con el límite de clientes o usuarios del plan, o con una funcionalidad que no incluye.
 * Para el admin de agencia vende el upgrade (CTA a /plan); Enterprise y superadmin solo informan.
 */
export const PlanLimitDialog = ({ open, onClose, kind, planId, planName, limit, agencyName, detail, plans }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const agencyPlan = user?.agencyPlan;
  const isSuperadmin = user?.role === "superadmin";
  const plan = planId || agencyPlan?.plan;
  const resolvedPlanName = planName || agencyPlan?.plan_name || "";
  const isClients = kind === "clients";
  const isFeature = kind === "feature";

  const resolvedLimit = limit ?? (isClients ? agencyPlan?.max_clients : agencyPlan?.users_limit);
  const freeWithSelection = plan === "free" && Boolean(agencyPlan?.selection_completed);
  // Solo el admin de agencia contrata; sin planes que la incluyan no hay nada que vender
  const infoOnly =
    !PLANS_ON_SALE ||
    (isFeature ? user?.role !== "agency_admin" || !plans?.length : isSuperadmin || (isClients && plan === "enterprise"));

  const title = isFeature
    ? isSuperadmin
      ? "El plan de la agencia no incluye esta funcionalidad"
      : "Tu plan no te permite usar esta funcionalidad"
    : isSuperadmin
    ? "La agencia ha alcanzado su límite"
    : isClients
      ? "Has alcanzado el límite de clientes"
      : "Has alcanzado el límite de usuarios";

  const body = (() => {
    if (!PLANS_ON_SALE && !isSuperadmin) {
      if (isFeature) return detail;
      return isClients
        ? `Tu plan ${resolvedPlanName} incluye hasta ${resolvedLimit} clientes con acceso completo.`
        : `Tu plan ${resolvedPlanName} incluye ${resolvedLimit} ${resolvedLimit === 1 ? "usuario" : "usuarios"}.`;
    }
    if (isFeature) {
      if (!plans?.length) return detail;
      const availability = `Disponible desde el plan ${PLAN_NAMES[plans[0]]}.`;
      if (isSuperadmin) return `${detail} ${availability}`;
      return user?.role === "agency_admin"
        ? `${detail} ${availability} Mejora tu plan para desbloquearla.`
        : `${detail} Pide al administrador de tu agencia que mejore el plan para desbloquearla.`;
    }
    if (isSuperadmin) {
      return `${agencyName || "La agencia"} ha alcanzado el límite de ${isClients ? "clientes" : "usuarios"} de su plan ${resolvedPlanName} (${resolvedLimit}).`;
    }
    if (isClients) {
      if (plan === "enterprise") {
        return `Tu plan Enterprise incluye hasta ${resolvedLimit} clientes activos. Si necesitas más, escríbenos desde el botón de soporte.`;
      }
      if (freeWithSelection) {
        return `En el plan Free puedes gestionar ${resolvedLimit} clientes con acceso completo. Mejora tu plan para crear clientes nuevos.`;
      }
      return `Tu plan ${resolvedPlanName} incluye hasta ${resolvedLimit} clientes activos. Para añadir más, mejora tu plan.`;
    }
    const included = agencyPlan?.included_users ?? resolvedLimit;
    const extra = agencyPlan?.extra_users ?? 0;
    const includedText = `${included} ${included === 1 ? "usuario" : "usuarios"}`;
    const extraText = extra > 0 ? ` y tienes ${extra} ${extra === 1 ? "adicional" : "adicionales"}` : "";
    return `Tu plan ${resolvedPlanName} incluye ${includedText}${extraText}. Para invitar a más miembros del equipo, mejora tu plan o añade usuarios adicionales.`;
  })();

  const showArchiveHint = isClients && !isSuperadmin && !freeWithSelection;

  const goToPlans = () => {
    onClose?.(false);
    navigate(PLAN_PATH);
  };

  // Las plazas se compran antes de invitar; no aplica en prueba (usuarios ilimitados)
  const canBuySeats = kind === "users" && !isSuperadmin && plan !== "trial";
  const goToSeats = () => {
    onClose?.(false);
    navigate(`${PLAN_PATH}?add_users=1`);
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xs"
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {infoOnly ? (
            <Button onClick={() => onClose?.(false)}>Entendido</Button>
          ) : (
            <>
              {canBuySeats ? (
                <>
                  <Button variant="outline" onClick={goToPlans}>Ver planes</Button>
                  <Button onClick={goToSeats}>Añadir usuarios</Button>
                </>
              ) : (
                <Button onClick={goToPlans}>Ver planes</Button>
              )}
            </>
          )}
        </Stack>
      }
    >
      <Stack spacing={2}>
        <AlertNote variant="warning">{body}</AlertNote>
        {showArchiveHint && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            También puedes archivar un cliente que ya no gestiones para liberar una plaza.
          </Typography>
        )}
      </Stack>
    </UnifiedModal>
  );
};

PlanLimitDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(["clients", "users", "feature"]).isRequired,
  planId: PropTypes.string,
  planName: PropTypes.string,
  limit: PropTypes.number,
  agencyName: PropTypes.string,
  detail: PropTypes.string,
  plans: PropTypes.arrayOf(PropTypes.string),
};
